import React, { useMemo, useState } from 'react';
import { Button, Grid, Modal, Typography } from 'antd';
import { DragSourceMonitor, useDrag } from 'react-dnd';
import styled from 'styled-components';

import { FilePdfFilled } from '@ant-design/icons';

import { useGetCandidateStorageFile } from '../common/useDownloadCandidateFile';
import { useGetFileStorageToken } from '../common/useGetFilestorageToken';
import { UploadButton } from '../features/candidate-details/CandidatesPersonalData';
import { AttachmentType } from '../features/emails/Emails.type';
import Icon, { iconColors } from '../icons/Icon';

import { StyledColumn } from './StyledColumn';
import { StyledRow } from './StyledRow';

const AttachmentContainer = styled.div`
  height: 75px;
  width: 55px;
  color: #bfbfbf;
  display: flex;
  justify-content: center;
  cursor: pointer;
`;

interface DropResult {
  name: string;
}

type AttachmentProps = {
  attachment: AttachmentType;
  setCvAttachement?: (id: string) => void;
  candidateId?: string;
  pickMode?: boolean;
  onPick?: (fileId: string) => void;
  isChosen?: boolean;
};

export const Attachment: React.FC<AttachmentProps> = ({
  attachment,
  setCvAttachement,
  pickMode = false,
  onPick,
  isChosen = false,
}) => {
  const screens = Grid.useBreakpoint();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, drag] = useDrag({
    type: 'email',
    item: { name: attachment.name, fileId: attachment.fileId },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor: DragSourceMonitor) => {
      const dropResult = monitor.getDropResult<DropResult>();
      if (dropResult && dropResult.name === 'candidate attachments') setCvAttachement?.(item.fileId);
    },
  });

  const { data: token, isLoading: tokenLoading } = useGetFileStorageToken(attachment.fileId);

  const { data: fileToShow, isLoading: fileLoading } = useGetCandidateStorageFile(
    attachment.fileId || '',
    token?.token || ''
  );

  const fileURL = useMemo(() => {
    if (fileToShow) {
      return URL.createObjectURL(fileToShow);
    }
  }, [fileToShow]);

  const isImage = ['image/png', 'image/jpeg'].includes(fileToShow?.type || '');
  const isPdf = fileToShow?.type === 'application/pdf';
  const isFilePreviewAvailable = screens.xl && (isImage || isPdf);

  const ModalFileContainer: React.FC = () => {
    const style = { height: '90vh', width: '580px' };
    return (
      <>
        {fileToShow?.type === 'application/pdf' ? (
          <embed style={style} src={fileURL} />
        ) : (
          <img src={fileURL} style={{ ...style, objectFit: 'contain' }}></img>
        )}
      </>
    );
  };

  const ModalContent: React.FC = () => {
    return (
      <>
        {!isFilePreviewAvailable && !tokenLoading && !fileLoading ? (
          <StyledColumn width="100%" flexGrow="1" justifyContent="center">
            <div>Preview is not available.</div>
            <StyledRow gap="0.5rem">
              You can
              <UploadButton>
                <a style={{ color: '#004ba0' }} href={fileURL}>
                  download
                </a>
              </UploadButton>
              file instead.
            </StyledRow>
          </StyledColumn>
        ) : (
          <ModalFileContainer />
        )}
      </>
    );
  };

  const handlePick = (fileId: string) => {
    onPick?.(fileId);
  };

  const chosenStyle = { backgroundColor: 'rgba(77, 136, 255, 0.1)', borderRadius: '6px', alignContent: 'center' };

  const Thumbnail: React.FC = () => (
    <>
      {isImage && <img src={fileURL} style={{ height: '60px', objectFit: 'contain' }}></img>}
      {isPdf && <FilePdfFilled style={{ fontSize: '60px' }} />}
    </>
  );

  return (
    <div style={{ position: 'relative' }}>
      <StyledColumn
        ref={drag}
        gap="0.5rem"
        justifyContent="center"
        style={isChosen && pickMode ? { ...chosenStyle, alignItems: 'center' } : { alignItems: 'center' }}
      >
        <AttachmentContainer onClick={() => (pickMode ? handlePick(attachment.fileId) : setIsModalOpen(true))}>
          <Thumbnail />
        </AttachmentContainer>

        <div style={{ display: 'flex', fontSize: '12px', width: '70px', justifyContent: 'center' }}>
          <Typography.Text>{attachment.name}</Typography.Text>
        </div>

        {isChosen && (
          <div style={{ position: 'absolute', right: '0px', top: '0px' }}>
            <Icon name="HrReview" style={{ color: iconColors.green }} />
          </div>
        )}
      </StyledColumn>

      <Modal
        closable={false}
        open={isModalOpen && !pickMode}
        footer={
          <StyledRow style={{ justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              onClick={() => {
                setIsModalOpen(false);
              }}
            >
              Close
            </Button>
          </StyledRow>
        }
        centered={true}
        width="630px"
      >
        <ModalContent />
      </Modal>
    </div>
  );
};
